import { type KeyboardEvent, useEffect, useRef, useState } from "react";
import { Check, Loader2, Pencil, X } from "lucide-react";
import { cn } from "@/lib/cn";

type Opcao = { value: string; label: string };

type Props = {
  label: string;
  /** Valor atual; string vazia = vazio. */
  value: string;
  onSave: (novo: string) => void | Promise<void>;
  type?: "text" | "textarea" | "date" | "select";
  placeholder?: string;
  formatDisplay?: (value: string) => string;
  /** Sanitiza antes de salvar (ex: CPF → só dígitos). */
  normalize?: (raw: string) => string;
  /** Máscara enquanto digita. */
  maskInput?: (value: string) => string;
  options?: Opcao[];
  inputMode?: "text" | "numeric" | "email" | "tel";
  maxLength?: number;
  /** Campo obrigatório: vazio volta ao valor anterior sem salvar. */
  obrigatorio?: boolean;
  /** Valor mostrado em cinza quando vazio (ex: "Usa o celular"). */
  vazio?: string;
  somenteLeitura?: boolean;
};

/**
 * Linha da aba Dados: rótulo à esquerda, valor e o lápis à direita. Clique edita no lugar; Enter ou
 * sair do campo salva, Esc cancela.
 */
export function LinhaEditavel({
  label,
  value,
  onSave,
  type = "text",
  placeholder,
  formatDisplay,
  normalize,
  maskInput,
  options,
  inputMode,
  maxLength,
  obrigatorio,
  vazio,
  somenteLeitura,
}: Props) {
  const [editando, setEditando] = useState(false);
  const [rascunho, setRascunho] = useState(value);
  const [salvando, setSalvando] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null>(null);
  // Evita que o blur depois do clique no check salve duas vezes
  const salvandoRef = useRef(false);

  useEffect(() => {
    if (!editando) setRascunho(value);
  }, [value, editando]);

  const comecar = () => {
    if (salvando || somenteLeitura) return;
    setRascunho(value);
    setEditando(true);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      el?.focus();
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) el.select();
    });
  };

  const cancelar = () => {
    setRascunho(value);
    setEditando(false);
  };

  const salvar = async () => {
    salvandoRef.current = true;
    const final = normalize ? normalize(rascunho) : rascunho;
    if ((obrigatorio && !final.trim()) || final === value) {
      cancelar();
      salvandoRef.current = false;
      return;
    }
    setSalvando(true);
    try {
      await onSave(final);
      setEditando(false);
    } finally {
      setSalvando(false);
      salvandoRef.current = false;
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      cancelar();
    } else if (e.key === "Enter" && (type !== "textarea" || e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      salvar();
    }
  };
  const aoSair = () => {
    if (!salvandoRef.current) salvar();
  };
  const mudar = (raw: string) => setRascunho(maskInput ? maskInput(raw) : raw);
  const ref = (el: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null) => {
    inputRef.current = el;
  };
  const campo = "w-full bg-transparent text-[13px] font-medium text-zinc-900 outline-none placeholder:text-zinc-400 dark:text-foreground dark:placeholder:text-muted-foreground";

  const rotulo = (
    <span className={cn("w-[42%] shrink-0 text-[12.5px] text-zinc-500 dark:text-muted-foreground", type === "textarea" ? "pt-0.5" : "")}>{label}</span>
  );

  if (editando) {
    return (
      <div className="flex items-start gap-3 rounded-md bg-brand-50/60 px-2.5 py-1.5 ring-1 ring-brand-300 dark:bg-primary/10 dark:ring-primary/40">
        {rotulo}
        <div className="min-w-0 flex-1">
          {type === "textarea" ? (
            <textarea ref={ref} value={rascunho} onChange={(e) => mudar(e.target.value)} onKeyDown={onKeyDown} onBlur={aoSair} disabled={salvando} rows={4} placeholder={placeholder} maxLength={maxLength} className={cn(campo, "resize-none")} />
          ) : type === "select" ? (
            <select ref={ref} value={rascunho} onChange={(e) => setRascunho(e.target.value)} onKeyDown={onKeyDown} onBlur={aoSair} disabled={salvando} className={cn(campo, "cursor-pointer")}>
              <option value="">—</option>
              {options?.map((o) => (
                <option key={o.value} value={o.value} className="bg-white dark:bg-popover">
                  {o.label}
                </option>
              ))}
            </select>
          ) : (
            <input ref={ref} type={type === "date" ? "date" : "text"} value={rascunho} onChange={(e) => mudar(e.target.value)} onKeyDown={onKeyDown} onBlur={aoSair} disabled={salvando} inputMode={inputMode} placeholder={placeholder} maxLength={maxLength} className={campo} />
          )}
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              salvandoRef.current = true;
              salvar();
            }}
            disabled={salvando}
            className="flex h-6 w-6 items-center justify-center rounded-md text-emerald-600 hover:bg-emerald-100 disabled:opacity-50 dark:hover:bg-emerald-500/15"
            title="Salvar (Enter)"
            aria-label="Salvar"
          >
            {salvando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          </button>
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              cancelar();
            }}
            disabled={salvando}
            className="flex h-6 w-6 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100 disabled:opacity-50 dark:hover:bg-muted"
            title="Cancelar (Esc)"
            aria-label="Cancelar"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }

  const exibido = value ? (formatDisplay ? formatDisplay(value) : value) : "";
  return (
    <div
      role={somenteLeitura ? undefined : "button"}
      tabIndex={somenteLeitura ? undefined : 0}
      aria-label={somenteLeitura ? undefined : `Editar ${label}`}
      onClick={comecar}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          comecar();
        }
      }}
      className={cn(
        "group flex items-start gap-3 rounded-md px-2.5 py-1.5",
        !somenteLeitura && "cursor-pointer transition-colors hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 dark:hover:bg-muted/40",
      )}
    >
      {rotulo}
      <span
        className={cn(
          "min-w-0 flex-1 break-words text-[13px] font-medium",
          exibido ? "text-zinc-900 dark:text-foreground" : "text-zinc-300 dark:text-muted-foreground/50",
          type === "textarea" && "whitespace-pre-wrap",
        )}
      >
        {exibido || vazio || "—"}
      </span>
      {!somenteLeitura && (
        <Pencil aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0 text-zinc-300 transition-colors group-hover:text-brand-600 dark:text-muted-foreground/50" />
      )}
    </div>
  );
}
